import React from 'react';
import { SearchFilters } from './SearchFilters';
import { SearchResults } from './SearchResults';
import { useSearch } from './context/SearchContext';
import { useNavigate } from 'react-router-dom';
import ErrorBoundary from '../ErrorBoundary';
import { useIsMobile } from '@/hooks/use-mobile';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { FilterIcon } from 'lucide-react';

const SearchErrorFallback = () => {
  return (
    <div className="text-center py-12">
      <h3 className="text-lg font-medium text-gray-900 mb-2">Search results couldn't be loaded</h3>
      <p className="text-gray-500 mb-4">
        We encountered an issue with the search functionality. Please try again with different filters.
      </p>
      <button 
        onClick={() => window.location.reload()}
        className="text-primary hover:text-primary/80 transition-colors"
      >
        Refresh
      </button>
    </div>
  );
};

export const SearchLayout = () => {
  const searchContext = useSearch();
  const isMobile = useIsMobile();
  
  const { 
    destinations = [], 
    hosts = [], 
    loading = false, 
    climate = null, 
    hasBeach = null, 
    safety = null, 
    region = null,
    priceRange = [0, 200],
    sortBy = '', 
    costType = 'avg_accommodation_cost',
    handleFilterChange, 
    clearFilters, 
    currentPage = 1,
    totalCount = 0,
    setCurrentPage
  } = searchContext || {};
  
  const navigate = useNavigate();
  const ITEMS_PER_PAGE = 9;

  const handleDestinationClick = (id: string) => {
    navigate(`/destinations/${id}`);
  };

  const handlePageChange = (newPage: number) => {
    if (typeof setCurrentPage === 'function') {
      setCurrentPage(newPage);
      window.scrollTo(0, 0);
    }
  };

  const handleFilterChangeWrapper = (filter: string, value: any) => {
    if (typeof handleFilterChange === 'function') {
      handleFilterChange(filter, value);
    }
  };

  const handleClearFilters = () => {
    if (typeof clearFilters === 'function') {
      clearFilters();
    }
  };

  const renderFilters = () => (
    <SearchFilters
      climate={climate}
      hasBeach={hasBeach}
      safety={safety}
      region={region}
      priceRange={priceRange}
      sortBy={sortBy}
      costType={costType}
      onFilterChange={handleFilterChangeWrapper}
      onClearFilters={handleClearFilters}
    />
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
      {isMobile ? (
        <div className="sticky top-16 z-10 bg-white p-4 border-b">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" className="w-full flex items-center gap-2">
                <FilterIcon className="h-4 w-4" />
                <span>Filters</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[85vw] sm:w-[385px] overflow-y-auto">
              <div className="py-6">
                {renderFilters()}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      ) : (
        <aside className="lg:col-span-1">
          {renderFilters()}
        </aside>
      )}
      <main className={isMobile ? "col-span-1" : "lg:col-span-3"}>
        <ErrorBoundary fallback={<SearchErrorFallback />}>
          <SearchResults
            destinations={destinations}
            hosts={hosts}
            loading={loading}
            onDestinationClick={handleDestinationClick}
            currentPage={currentPage}
            totalCount={totalCount}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={handlePageChange}
          />
        </ErrorBoundary>
      </main>
    </div>
  );
};
